/**
 * Admin entry-point gating for the portal.
 *
 * D10 in `_docs/web-portal-navigation.md`:
 *   "Admin access = isSuperadmin === true OR granular permission set"
 *
 * D11:
 *   Portal account menu surfaces an Admin link when this returns `true`.
 *
 * `adminPermissions` is added to the JWT by task #8 (server-side).
 * Until then it is `undefined` at runtime, and this function falls back
 * to the `isSuperadmin` check alone.
 */
import type { SessionUser } from "@pontimed/auth-client";

export function hasAdminAccess(user: SessionUser | null | undefined): boolean {
  if (!user) return false;
  if (user.isSuperadmin === true) return true;
  return (user.adminPermissions?.length ?? 0) > 0;
}

/**
 * Resolve admin app URL. Explicit env override wins; otherwise we infer
 * from the current host so the unified-Caddy dev (portal.pontimed.local
 * → admin.pontimed.local) doesn't accidentally bounce the user out to
 * production admin.
 *
 * Production default: https://admin.pontimed.app — cookie shared on
 * `.pontimed.app` so no re-login is needed (D11).
 *
 * Both `NEXT_PUBLIC_ADMIN_URL` (canonical, architecture rule 1 envPrefix)
 * and `VITE_ADMIN_URL` (Vite-native fallback) are accepted.
 */
export function getAdminUrl(): string {
  const explicit =
    (import.meta.env.NEXT_PUBLIC_ADMIN_URL as string | undefined) ??
    (import.meta.env.VITE_ADMIN_URL as string | undefined);
  if (explicit) return explicit;

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    // Unified-Caddy dev — portal.pontimed.local → admin.pontimed.local.
    if (host.endsWith(".pontimed.local")) return "https://admin.pontimed.local";
    // Plain localhost dev — apps/admin runs on Vite at :5174.
    if (host === "localhost" || host === "127.0.0.1") return "http://localhost:5174";
    // Any other *.pontimed.app subdomain (incl. portal-staging.pontimed.app)
    // points at the matching admin subdomain on the same root.
    if (host.endsWith(".pontimed.app")) {
      const root = host.split(".").slice(-2).join(".");
      return `https://admin.${root}`;
    }
  }

  return "https://admin.pontimed.app";
}

/**
 * `href` for a notification's `data.link` in the portal bell. Superadmin
 * notifications carry `/admin/<route>`, and the admin SPA serves its routes at
 * its own root, so those open on the admin origin; on the portal they were a
 * 404. Everything else is returned unchanged.
 */
export function notificationLinkHref(link: string): string {
  if (!/^\/admin(?=$|[/?#])/.test(link)) return link;
  const rest = link.slice("/admin".length);
  return `${getAdminUrl().replace(/\/+$/, "")}${rest.startsWith("/") ? rest : `/${rest}`}`;
}
